import Guid from "@/app/types/common/guid";
import type { Maybe } from "@/components/utils/typeHelpers";

class GuidIterableIterator implements IterableIterator<Guid> {
  private _iterator: Iterator<string>;

  constructor(strIterator: Iterator<string>) {
    this._iterator = strIterator;
  }

  next(...args: [] | [undefined]): IteratorResult<Guid> {
    const res = this._iterator.next();
    if (res.done) {
      return { done: true, value: null };
    } else {
      return { done: false, value: Guid.parseOrEmpty(res.value) };
    }
  }

  [Symbol.iterator](): IterableIterator<Guid> {
    return this;
  }
}

class GuidMapEntryIterableIterator<T> implements IterableIterator<[Guid, T]> {
  private _iterator: Iterator<[string, T]>;

  constructor(strIterator: Iterator<[string, T]>) {
    this._iterator = strIterator;
  }

  next(...args: [] | [undefined]): IteratorResult<[Guid, T], any> {
    const res = this._iterator.next();
    if (res.done) {
      return {
        done: true,
        value: null,
      };
    } else {
      const guid = Guid.parseOrEmpty(res.value[0]);
      return {
        done: false,
        value: [guid, res.value[1]],
      };
    }
  }

  [Symbol.iterator](): IterableIterator<[Guid, T]> {
    return this;
  }
}

class GuidEntryIterableIterator implements IterableIterator<[Guid, Guid]> {
  private _iterator: Iterator<[string, string]>;

  constructor(strIterator: Iterator<[string, string]>) {
    this._iterator = strIterator;
  }

  next(...args: [] | [undefined]): IteratorResult<[Guid, Guid], any> {
    const res = this._iterator.next();
    if (res.done) {
      return {
        done: true,
        value: null,
      };
    } else {
      const guid = Guid.parseOrEmpty(res.value[0]);
      return {
        done: false,
        value: [guid, guid],
      };
    }
  }

  [Symbol.iterator](): IterableIterator<[Guid, Guid]> {
    return this;
  }
}

export class GuidMap<T> implements Map<Guid, T> {
  readonly [Symbol.toStringTag] = "GuidMap";
  private _internalMap: Map<string, T> = new Map<string, T>();

  constructor(entries?: [Guid, T][] | null) {
    entries?.forEach((entry: [Guid, T]) => this.set(entry[0], entry[1]));
  }

  get size() {
    return this._internalMap.size;
  }

  [Symbol.iterator](): IterableIterator<[Guid, T]> {
    return this.entries();
  }

  clear(): void {
    this._internalMap.clear();
  }

  delete(key: Guid): boolean {
    return this._internalMap.delete(key.toString());
  }

  entries(): IterableIterator<[Guid, T]> {
    return new GuidMapEntryIterableIterator(this._internalMap.entries());
  }

  forEach(
    callbackfn: (value: T, key: Guid, map: Map<Guid, T>) => void,
    thisArg?: any
  ): void {
    this._internalMap.forEach((value: T, key: string, map: Map<string, T>) =>
      callbackfn.apply(thisArg, [value, Guid.parseOrEmpty(key), this])
    );
  }

  get(key: Maybe<Guid>): T | undefined {
    return key ? this._internalMap.get(key.toString()) : undefined;
  }

  has(key: Maybe<Guid>): boolean {
    return !!key && this._internalMap.has(key.toString());
  }

  keys(): IterableIterator<Guid> {
    return new GuidIterableIterator(this._internalMap.keys());
  }

  set(key: Guid, value: T): this {
    this._internalMap.set(key.toString(), value);
    return this;
  }

  values(): IterableIterator<T> {
    return this._internalMap.values();
  }

  kvps() {
    return [...this.entries()].map((x) => ({ key: x[0], value: x[1] }));
  }
}

/**
 * Note a true lodash Map, but just the parts of the interface we use a lot.
 */
export class GuidQMap<T> {
  private _mapForNonNull = new GuidMap<T>();
  private _nullValue = undefined as T | undefined;
  private _hasNull = false;

  constructor(entries?: [Guid | null, T][] | null) {
    this._mapForNonNull = new GuidMap(
      entries?.filter((x) => x[0] !== null)?.map((x) => [x[0]!, x[1]])
    );
    const nullEntry = entries?.find((x) => x[0] === null);
    if (nullEntry) {
      this._hasNull = true;
      this._nullValue = nullEntry[1];
    }
  }

  get size() {
    return this._mapForNonNull.size + (this._hasNull ? 1 : 0);
  }

  clear(): void {
    this._mapForNonNull.clear();
    this._nullValue = undefined;
    this._hasNull = false;
  }

  delete(key: Guid | null): boolean {
    if (key) {
      return this._mapForNonNull.delete(key);
    }
    const hadNull = this._hasNull;
    this._hasNull = false;
    this._nullValue = undefined;
    return hadNull;
  }

  get(key: Guid | null): T | undefined {
    if (key === null) {
      return this._hasNull ? this._nullValue : undefined;
    }
    return this._mapForNonNull.get(key);
  }

  has(key: Guid | null): boolean {
    return key === null ? this._hasNull : this._mapForNonNull.has(key);
  }

  set(key: Guid | null, value: T) {
    if (key === null) {
      this._hasNull = true;
      this._nullValue = value;
    } else {
      this._mapForNonNull.set(key, value);
    }
  }

  kvps() {
    return (
      this._hasNull
        ? [{ key: null as Guid | null, value: this._nullValue as T }]
        : []
    ).concat(this._mapForNonNull.kvps());
  }
}

/**
 * Type guard to send to .filter functions to cast away null/undefined
 */
function isNotNullOrUndefined(val: Maybe<Guid>): val is Guid {
  return !!val;
}

/**
 * A Set which takes Guids as keys, and checks for equality of the string value of the guid.
 * This works because Guid.value is always defined..
 *
 * NB: A Set in JS is as efficient in terms of time complexity as a Map.
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Set#description
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Map#description
 */
export class GuidSet implements Set<Guid> {
  readonly [Symbol.toStringTag] = "GuidSet";
  private readonly _internalSet = new Set<string>();

  constructor(entries?: readonly Guid[] | null) {
    entries?.forEach((entry: Guid) => this.add(entry));
  }

  get size(): number {
    return this._internalSet.size;
  }

  [Symbol.iterator](): IterableIterator<Guid> {
    return new GuidIterableIterator(this._internalSet[Symbol.iterator]());
  }

  add(value: Guid): this {
    this._internalSet.add(value.toString());
    return this;
  }

  clear(): void {
    this._internalSet.clear();
  }

  delete(value: Guid): boolean {
    return this._internalSet.delete(value.toString());
  }

  entries(): IterableIterator<[Guid, Guid]> {
    return new GuidEntryIterableIterator(this._internalSet.entries());
  }

  forEach(
    callbackfn: (value: Guid, value2: Guid, set: Set<Guid>) => void,
    thisArg?: any
  ): void {
    this._internalSet.forEach((guidStr: string) => {
      const guid = Guid.parseOrEmpty(guidStr);
      callbackfn.apply(thisArg, [guid, guid, this]);
    });
  }

  has(value: Guid): boolean {
    return this._internalSet.has(value.toString());
  }

  keys(): IterableIterator<Guid> {
    return new GuidIterableIterator(this._internalSet.keys());
  }

  values(): IterableIterator<Guid> {
    return this.keys();
  }

  intersect(other: Guid[]): GuidSet {
    return new GuidSet(other.filter((x) => this.has(x)));
  }

  toArray(): Guid[] {
    return [...this];
  }

  public static fromStrings(strings: readonly Maybe<string>[]) {
    return new GuidSet(
      strings.map((x) => Guid.tryParse(x)).filter(isNotNullOrUndefined)
    );
  }
}
